import { Paper, styled } from "@mui/material";
import {
    GetFieldLookup,
    GetFieldLookupItem,
    useFormMetadata,
    useFormSave,
    useLookups,
    useSnackbar,
    type ILookup,
} from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { CommissionTypes, CommissionValueTypes, FormDefinitions, ProjectCommission } from "../api/dtos";
import { StyledButton } from "../components/StyledButton";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn, AppScalarValue } from "../components/tables/AppTable.types";
import TitleBox from "../components/TitleBox";
import ProjectContext from "../contexts/data/ProjectContext";
import useProjectCommissionsByProjectId from "../hooks/data/useProjectCommissionsByProjectId";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

function setCommissionField<K extends keyof ProjectCommission>(
    row: ProjectCommission,
    key: K,
    value: ProjectCommission[K],
): ProjectCommission {
    const updated = Object.assign(Object.create(Object.getPrototypeOf(row)) as ProjectCommission, row);

    updated[key] = value;

    return updated;
}

function lookupOptions(lookups: ILookup[] | null | undefined, propertyName: string) {
    return (GetFieldLookup(lookups, propertyName)?.items ?? []).flatMap((item) => {
        if (item.id === null || item.id === undefined || item.id === "") {
            return [];
        }

        const value = typeof item.id === "number" ? item.id : Number(item.id);

        return Number.isFinite(value)
            ? [
                  {
                      value,
                      label: item.value?.toString() ?? String(value),
                  },
              ]
            : [];
    });
}

function lookupLabel(lookups: ILookup[] | null | undefined, propertyName: string, value: AppScalarValue): string {
    if (value === null || value === undefined) {
        return "";
    }

    return GetFieldLookupItem(lookups, propertyName, value)?.value?.toString() ?? String(value);
}

function localDateTime(value: string | undefined): string | undefined {
    if (!value) {
        return value;
    }

    const parsed = DateTime.fromISO(value);

    return parsed.isValid
        ? (parsed.toISO({
              includeOffset: false,
          }) ?? value)
        : value;
}

export const buildProjectCommissionColumns = (
    lookups: ILookup[] | null | undefined,
): AppColumn<ProjectCommission>[] => {
    const lookupColumn = (
        id: "commissionType" | "commissionValueType",
        label: string,
        lookupName: string,
        accessor: (row: ProjectCommission) => number | undefined,
        setValue: (row: ProjectCommission, value: AppScalarValue) => ProjectCommission,
    ): AppColumn<ProjectCommission> => ({
        id,
        label,
        accessor,
        scalarType: "lookup",
        flex: 1,
        lookup: {
            options: lookupOptions(lookups, lookupName),
            labelFor: (value) => lookupLabel(lookups, lookupName, value),
        },
        exportAccessor: ({ value }) => value,
        setValue,
    });

    return [
        {
            id: "commission",
            label: "Commission",
            accessor: (row) => row.commission,
            scalarType: "number",
            flex: 1,
            setValue: (row, value) =>
                setCommissionField(row, "commission", typeof value === "number" ? value : undefined),
        },
        {
            id: "dateEffective",
            label: "Date Effective",
            accessor: (row) => localDateTime(row.dateEffective),
            scalarType: "date",
            dateSerialization: "localDateTime",
            flex: 1,
            formatter: (value) =>
                typeof value === "string" && value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "",
            setValue: (row, value) =>
                setCommissionField(row, "dateEffective", typeof value === "string" ? value : undefined),
        },
        lookupColumn(
            "commissionType",
            "Commission Type",
            "CommissionType",
            (row) => row.commissionType,
            (row, value) =>
                setCommissionField(
                    row,
                    "commissionType",
                    typeof value === "number" ? (value as CommissionTypes) : undefined,
                ),
        ),
        lookupColumn(
            "commissionValueType",
            "Commission Paid As",
            "CommissionValueType",
            (row) => row.commissionValueType,
            (row, value) =>
                setCommissionField(
                    row,
                    "commissionValueType",
                    typeof value === "number" ? (value as CommissionValueTypes) : undefined,
                ),
        ),
    ];
};

function errorMessages(reason: unknown): string[] {
    if (typeof reason !== "object" || reason === null) {
        return ["Unable to save project commission."];
    }

    const responseStatus = (reason as { responseStatus?: { errors?: readonly { message?: string }[] } }).responseStatus;
    const messages = responseStatus?.errors
        ?.map((error) => error.message)
        .filter((message): message is string => Boolean(message));

    return messages?.length ? messages : ["Unable to save project commission."];
}

export const BulkEditProjectCommissions: FC = () => {
    const projectCommissionMetadata = useFormMetadata(FormDefinitions.ProjectCommission);
    const save = useFormSave(projectCommissionMetadata);
    const { data: lookups } = useLookups(projectCommissionMetadata);
    const params = useParams();
    const projectId = Number(params.projectId);
    const {
        data: projectCommissions,
        loading: projectCommissionsLoading,
        refresh: projectCommissionsRefresh,
    } = useProjectCommissionsByProjectId(projectId);
    const { data: project } = useContext(ProjectContext);
    const snackbar = useSnackbar();

    const submit = async (draft: ProjectCommission, _original: ProjectCommission) => {
        try {
            const persisted = (await save(draft)) as ProjectCommission | undefined;

            await projectCommissionsRefresh();

            return persisted ?? draft;
        } catch (reason) {
            for (const message of errorMessages(reason))
                snackbar.enqueueSnackbar(message, {
                    variant: "error-critical",
                });
            throw reason;
        }
    };

    const addNewProjectCommission = async () => {
        const draft = new ProjectCommission({
            projectId,
        });
        const persisted = (await save(draft)) as ProjectCommission | undefined;

        await projectCommissionsRefresh();

        return persisted ?? draft;
    };

    const columns = useMemo(() => buildProjectCommissionColumns(lookups), [lookups]);
    const exportTitle = `${project?.name} - Commissions`;

    return (
        <>
            <Outlet />
            <TitleBox display="flex" justifyContent="flex-end">
                <StyledButton variant="contained" onClick={addNewProjectCommission}>
                    Add Commission
                </StyledButton>
            </TitleBox>
            <StyledPaper>
                <CollapsibleAppTable
                    density="compact"
                    startCollapsed={false}
                    title="Project Commissions"
                    loading={projectCommissionsLoading}
                    columns={columns}
                    rows={projectCommissions ?? []}
                    getRowId={(commission) => {
                        if (commission.id == null) {
                            throw new Error("Project commission is missing its domain ID");
                        }

                        return commission.id;
                    }}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    groupingEnabled={false}
                    editing={{
                        mode: "row",
                        onCommit: submit,
                    }}
                    export={{
                        fileName: exportTitle,
                        exportTitle,
                        includeDateTime: true,
                    }}
                />
            </StyledPaper>
        </>
    );
};

export default BulkEditProjectCommissions;
